import { useState, useCallback } from "react";
import { Combine, Upload, Download, Trash2, FileText, Loader2, GripVertical, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import SEOHead from "@/components/SEOHead";
import FAQSection from "@/components/FAQSection";
import RelatedTools from "@/components/RelatedTools";
import ToolContentSection from "@/components/ToolContentSection";
import { toolContent } from "@/data/toolContent";
import { downloadFile } from "@/utils/downloadHelper";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const faqs = [
  {
    question: "Is the PDF merge tool free?",
    answer: "Yes, our PDF merger is completely free. No signup, no payment, no limits on the number of files you can merge."
  },
  {
    question: "How many PDFs can I merge at once?",
    answer: "You can merge unlimited PDFs in one go. Simply upload all your files, arrange them in your preferred order, and merge."
  },
  {
    question: "Will the quality be affected after merging?",
    answer: "No, merging PDFs does not affect quality. The original resolution, fonts, and formatting are preserved in the merged document."
  },
  {
    question: "Can I change the order of PDFs before merging?",
    answer: "Yes, you can drag and drop files to rearrange them in any order before merging."
  },
  {
    question: "Are my files secure?",
    answer: "Absolutely. All uploaded files are automatically deleted after 30 minutes. We never store or access your documents."
  }
];

const PdfMergePage = () => {
  const [files, setFiles] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files).filter(
      (f) => f.type === "application/pdf"
    );
    if (droppedFiles.length === 0) {
      toast.error("Please upload PDF files only");
      return;
    }
    setFiles((prev) => [...prev, ...droppedFiles]);
    setResult(null);
  }, []);

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files).filter(
      (f) => f.type === "application/pdf"
    );
    if (selectedFiles.length === 0) {
      toast.error("Please upload PDF files only");
      return;
    }
    setFiles((prev) => [...prev, ...selectedFiles]);
    setResult(null);
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const moveFile = (fromIndex, direction) => {
    const toIndex = fromIndex + direction;
    if (toIndex < 0 || toIndex >= files.length) return;
    
    setFiles((prev) => {
      const newFiles = [...prev];
      const [removed] = newFiles.splice(fromIndex, 1);
      newFiles.splice(toIndex, 0, removed);
      return newFiles;
    });
  };

  const handleMerge = async () => {
    if (files.length < 2) {
      toast.error("Please select at least 2 PDF files to merge");
      return;
    }

    setIsProcessing(true);
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files", file);
    });

    try {
      const response = await axios.post(`${API}/pdf-merge`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.success) {
        setResult(response.data);
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Merge failed");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownload = async () => {
    if (result?.files?.length > 0) {
      try {
        const downloadUrl = `${API}/download/${result.files[0].id}`;
        await downloadFile(downloadUrl, result.files[0].name || "merged.pdf");
      } catch (error) {
        toast.error("Download failed");
      }
    }
  };

  const handleReset = () => {
    setFiles([]);
    setResult(null);
  };

  return (
    <>
      <SEOHead
        title="Merge PDF Files Online Free | No Signup"
        description="Combine multiple PDF files into one document. Free online PDF merger. Drag, drop, rearrange and merge. No signup required. Files auto-delete."
        canonicalPath="/pdf-merge"
        keywords="merge PDF, combine PDF, join PDF files, PDF merger free, online PDF combiner, merge multiple PDFs"
      />
      
      <div className="py-12 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8 animate-fade-in-up">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-black flex items-center justify-center">
              <Combine className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-3xl md:text-5xl font-semibold tracking-tight">
              Merge PDF
            </h1>
          </div>
          <p className="text-muted-foreground text-lg">
            Combine multiple PDF files into a single document
          </p>
        </div>

        {/* Upload Area */}
        {!result && (
          <div className="space-y-6 animate-fade-in-up animate-delay-100">
            <div
              className={`upload-zone ${isDragging ? "dragging" : ""}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => document.getElementById("file-input").click()}
              data-testid="upload-zone"
            >
              <input
                id="file-input"
                type="file"
                accept=".pdf"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
              <div className="w-16 h-16 bg-secondary flex items-center justify-center">
                <Upload className="w-8 h-8 text-muted-foreground" />
              </div>
              <div className="text-center">
                <p className="font-medium text-lg">Drop PDF files here</p>
                <p className="text-sm text-muted-foreground mt-1">
                  or click to browse (max 50MB each)
                </p>
              </div>
            </div>

            {/* Selected Files */}
            {files.length > 0 && (
              <div className="space-y-3" data-testid="selected-files">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium">
                    Selected PDFs ({files.length})
                  </h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setFiles([])}
                    className="text-muted-foreground hover:text-destructive"
                    data-testid="clear-all-button"
                  >
                    Clear All
                  </Button>
                </div>
                <div className="space-y-2">
                  {files.map((file, index) => (
                    <div
                      key={`${file.name}-${index}`}
                      className="file-item animate-fade-in-up"
                      style={{ animationDelay: `${index * 50}ms` }}
                      data-testid={`selected-file-${index}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => moveFile(index, -1)}
                            disabled={index === 0}
                            className="p-1 hover:bg-secondary disabled:opacity-30 transition-colors"
                            data-testid={`move-up-${index}`}
                          >
                            ↑
                          </button>
                          <button
                            onClick={() => moveFile(index, 1)}
                            disabled={index === files.length - 1}
                            className="p-1 hover:bg-secondary disabled:opacity-30 transition-colors"
                            data-testid={`move-down-${index}`}
                          >
                            ↓
                          </button>
                        </div>
                        <div className="w-10 h-10 bg-secondary flex items-center justify-center">
                          <span className="text-sm font-mono font-bold">{index + 1}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{file.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {(file.size / (1024 * 1024)).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => removeFile(index)}
                        className="p-2 hover:bg-destructive hover:text-white transition-colors"
                        data-testid={`remove-file-${index}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-muted-foreground">
                  Use arrows to reorder. PDFs will be merged in the order shown.
                </p>
              </div>
            )}

            {/* Merge Button */}
            <Button
              onClick={handleMerge}
              disabled={files.length < 2 || isProcessing}
              className="w-full h-14 bg-black hover:bg-black/90 text-white rounded-sm btn-hover-lift text-lg font-medium"
              data-testid="merge-button"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Merging...
                </>
              ) : (
                <>
                  <Combine className="w-5 h-5 mr-2" />
                  Merge {files.length > 0 ? `${files.length} PDFs` : "PDFs"}
                </>
              )}
            </Button>

            {files.length === 1 && (
              <p className="text-sm text-center text-muted-foreground">
                Add at least one more PDF to merge
              </p>
            )}
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="space-y-6 animate-fade-in-up" data-testid="result-section">
            <div className="border border-border p-8 text-center">
              <div className="w-20 h-20 bg-[#FF3B30] flex items-center justify-center mx-auto mb-6">
                <FileText className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl font-semibold tracking-tight mb-2">
                PDFs Merged Successfully
              </h2>
              <p className="text-muted-foreground mb-6">
                {result.files[0]?.filename}
              </p>
              <div className="flex justify-center gap-4">
                <Button
                  variant="outline"
                  onClick={handleReset}
                  className="rounded-sm"
                  data-testid="reset-button"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Start Over
                </Button>
                <Button
                  onClick={handleDownload}
                  className="bg-[#FF3B30] hover:bg-[#FF3B30]/90 rounded-sm btn-hover-lift"
                  data-testid="download-button"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
              </div>
            </div>

            <div className="bg-secondary p-4 text-center">
              <p className="text-sm text-muted-foreground">
                ⏱ File will be automatically deleted in 30 minutes
              </p>
            </div>
          </div>
        )}

        {/* FAQ Section */}
        <ToolContentSection toolKey="pdf-merge" content={toolContent["pdf-merge"]} />
        <FAQSection faqs={faqs} />

        {/* Related Tools */}
        <RelatedTools currentTool="pdf-merge" />
      </div>
    </div>
    </>
  );
};

export default PdfMergePage;
